'use client';

import { Button } from '@/core/components/ui/button';
import { ShoppingCart, Loader2, Check } from 'lucide-react';
import { useState, useEffect } from 'react';
import { toast } from '@/core/utils/toast';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { getPublicMediaUrl } from '@/core/utils/media';
import { trackAddToCart } from '@/core/utils/analytics';
import { usesConfigurator } from '@/core/utils/product-action';
import { applyB2BTiers, b2bTierPrice } from '@/core/utils/b2b-pricing';

import { useProductState } from '@/core/components/Builder/ProductContext';

export default function AddToCartButton({ product, variant, quantity = 1 }: { product: any, variant?: any, quantity?: number }) {
    const { t, currency } = useTranslation();
    const productContext = useProductState();
    const [status, setStatus] = useState<'idle' | 'loading' | 'added'>('idle');
    const [isInCart, setIsInCart] = useState(false);

    useEffect(() => {
        const checkCart = () => {
            const cart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
            const cartItemId = variant ? `${product.id}-${variant.id}` : `${product.id}`;
            setIsInCart(cart.some((item: any) => item.cartItemId === cartItemId));
        };
        checkCart();
        window.addEventListener('cart-updated', checkCart);
        return () => window.removeEventListener('cart-updated', checkCart);
    }, [product.id, variant]);

    const addToCart = () => {
        if (status === 'loading') return;
        if (usesConfigurator(product) && productContext?.configurableAddToCart) {
            productContext.configurableAddToCart();
            return;
        }
        setStatus('loading');
        try {
            const activePrice = variant 
                ? (variant.sales_price !== null ? variant.sales_price : variant.price)
                : (product.sales_price !== null ? product.sales_price : product.price);

            // Basic LocalStorage Cart Implementation
            const cart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
            
            // Unique key for cart: product_id + variant_id if it exists
            const cartItemId = variant ? `${product.id}-${variant.id}` : `${product.id}`;
            const existingItem = cart.find((item: any) => item.cartItemId === cartItemId);

            if (existingItem) {
                if (product.is_one_off) {
                    toast.error(t('product.one_off_limit', 'Ten produkt można dodać do koszyka tylko raz.'));
                    setStatus('idle');
                    return;
                }
                existingItem.quantity += quantity;
                Object.assign(existingItem, applyB2BTiers(existingItem));
            } else {
                const mainImage = product.images?.find((img: any) => img.is_main) || product.images?.[0];
                const imagePath = variant?.image_path || mainImage?.path;
                const imageUrl = imagePath ? getPublicMediaUrl(imagePath) : null;
                
                // Build a nice name for variant
                const variantName = variant 
                    ? ` (${Object.values(variant.attributes).join(', ')})`
                    : '';

                cart.push({
                    cartItemId,
                    id: product.id,
                    variant_id: variant?.id || null,
                    product_type: product.product_type,
                    name: product.name + variantName,
                    price: activePrice,
                    image: imageUrl,
                    slug: product.slug,
                    quantity: quantity,
                    bundle_items: product.bundle_items || null,
                    attributes: variant?.attributes || null,
                    is_one_off: !!product.is_one_off,
                    tax_rate: product.tax_rate ?? 23,
                    // Progi B2B (D9): koszyk przelicza cenę linii z ceny bazowej po zmianie ilości.
                    ...((variant || product).b2b_tiers?.length
                        ? { b2b_base_price: activePrice, b2b_tiers: (variant || product).b2b_tiers, price: b2bTierPrice(activePrice, (variant || product).b2b_tiers, quantity) }
                        : {}),
                });
            }


            localStorage.setItem('stag-cart', JSON.stringify(cart));
            setStatus('added');
            setTimeout(() => setStatus('idle'), 2000);

            // Track AddToCart event (Meta Pixel & Google dataLayer)
            trackAddToCart(product, variant, 1, currency || 'PLN');

            // Dispatch event for header update
            window.dispatchEvent(new Event('cart-updated'));
            toast.success(t('product.add_to_cart_success'));
        } catch (error) {
            console.error("Failed to add to cart", error);
            toast.error(t('product.add_to_cart_error'));
            setStatus('idle');
        }
    };

    const isOutOfStock = variant
        ? (variant.stock <= 0)
        : (product.stock <= 0);

    const isDisabled = isOutOfStock || status === 'loading' || (product.is_one_off && isInCart);

    return (
        <Button size="lg" className="w-full md:w-auto" onClick={addToCart} disabled={isDisabled}>
            {status === 'loading' ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            ) : status === 'added' ? (
                <Check className="mr-2 h-5 w-5" />
            ) : (
                <ShoppingCart className="mr-2 h-5 w-5" />
            )}
            {product.is_one_off && isInCart 
                ? t('product.already_in_cart', 'W koszyku') 
                : (status === 'added' 
                    ? t('product.added') 
                    : (product.product_type === 'bundle' ? t('product.add_bundle_to_cart', 'Dodaj zestaw do koszyka') : t('product.add_to_cart')))}
        </Button>
    );

}
